// Contradiction card — the agent flagged two existing nodes as
// conflicting on facts (NOT taste). User picks the resolution; the
// card calls apply with the chosen resolution string.
//
// Visual treatment is distinct from GraphProposalCard: amber/rose
// rail to signal "watch out" rather than the neutral slate rail of
// regular proposals.

import { Check, Loader2, ShieldX, Split, X, Zap } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { GraphProposal } from "@/lib/types";
import { cn } from "@/lib/utils";

type Resolution = "keep_both" | "delete_a" | "delete_b";

interface Props {
  proposal: GraphProposal;
  onApply: (resolution: Resolution) => Promise<void> | void;
  onReject: (userReason?: string) => Promise<void> | void;
  resolved?:
    | { kind: "applied"; resolution: Resolution; affected: number }
    | { kind: "rejected"; reason: string };
}

export function ContradictionCard({
  proposal,
  onApply,
  onReject,
  resolved,
}: Props) {
  const p = proposal.payload as {
    node_a_id?: string;
    node_b_id?: string;
    node_a_snapshot?: { category?: string; display_label?: string; content?: string };
    node_b_snapshot?: { category?: string; display_label?: string; content?: string };
    recommended_resolution?: Resolution;
  };
  const recommended = p.recommended_resolution ?? "keep_both";
  const [busy, setBusy] = useState<Resolution | "reject" | null>(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const handleApply = async (r: Resolution) => {
    setBusy(r);
    try {
      await onApply(r);
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async () => {
    setBusy("reject");
    try {
      await onReject(rejectReason.trim() || undefined);
    } finally {
      setBusy(null);
      setShowRejectInput(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-lg border border-amber-200 bg-amber-50/60 overflow-hidden relative pl-3"
      )}
    >
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-amber-500/80"
      />
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-start gap-3">
        <div className="mt-0.5 h-7 w-7 rounded-md bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-700">
          <Zap className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium tracking-wide uppercase text-amber-900/80">
            Contradiction flagged
          </div>
          <div className="text-sm font-medium text-foreground mt-0.5">
            The agent thinks these two nodes conflict
          </div>
        </div>
        {resolved && (
          <ResolvedBadge
            kind={resolved.kind}
            resolution={resolved.kind === "applied" ? resolved.resolution : undefined}
          />
        )}
      </div>

      {/* Reason */}
      {proposal.reason && (
        <div className="px-4 pb-2 text-sm text-foreground/90 italic">
          {proposal.reason}
        </div>
      )}

      {/* Side-by-side node snapshots */}
      <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-stretch">
        <NodeBox snapshot={p.node_a_snapshot} id={p.node_a_id} side="A" />
        <div className="hidden md:flex items-center justify-center text-amber-700/80">
          <Split className="h-4 w-4" />
        </div>
        <NodeBox snapshot={p.node_b_snapshot} id={p.node_b_id} side="B" />
      </div>

      {/* Recommendation hint */}
      {!resolved && (
        <div className="px-4 pb-2 text-[11px] text-muted-foreground">
          Agent recommends:{" "}
          <span className="text-foreground font-medium">
            {resolutionLabel(recommended)}
          </span>
          . You can pick differently below.
        </div>
      )}

      {/* Actions */}
      {!resolved && (
        <div className="px-4 py-3 border-t border-amber-200 bg-card/60 flex items-center gap-2 flex-wrap">
          <ResolutionButton
            r="keep_both"
            current={busy}
            recommended={recommended}
            onClick={() => handleApply("keep_both")}
          />
          <ResolutionButton
            r="delete_a"
            current={busy}
            recommended={recommended}
            onClick={() => handleApply("delete_a")}
            snapshot={p.node_a_snapshot}
          />
          <ResolutionButton
            r="delete_b"
            current={busy}
            recommended={recommended}
            onClick={() => handleApply("delete_b")}
            snapshot={p.node_b_snapshot}
          />
          {showRejectInput ? (
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Why dismiss? (optional)"
                className="flex-1 text-sm px-2 py-1 rounded-md border border-border bg-background outline-none focus:ring-2 focus:ring-amber-300"
                autoFocus
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleReject}
                disabled={busy !== null}
              >
                {busy === "reject" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  "Confirm"
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowRejectInput(false);
                  setRejectReason("");
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowRejectInput(true)}
              disabled={busy !== null}
              className="text-muted-foreground"
            >
              <ShieldX className="h-3.5 w-3.5 mr-1.5" />
              Not a conflict
            </Button>
          )}
        </div>
      )}

      {resolved && (
        <div className="px-4 py-2.5 border-t border-amber-200 bg-card/60 text-xs text-muted-foreground">
          {resolved.kind === "applied"
            ? `Applied — ${resolutionLabel(resolved.resolution)} (${resolved.affected} node${resolved.affected === 1 ? "" : "s"} touched).`
            : resolved.reason
              ? `Dismissed — "${resolved.reason}".`
              : "Dismissed (no conflict)."}
        </div>
      )}
    </div>
  );
}

function NodeBox({
  snapshot,
  id,
  side,
}: {
  snapshot:
    | { category?: string; display_label?: string; content?: string }
    | undefined;
  id: string | undefined;
  side: "A" | "B";
}) {
  return (
    <div className="rounded-md border border-amber-200/70 bg-card/80 p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center justify-between">
        <span>Node {side}</span>
        <span>{snapshot?.category ?? ""}</span>
      </div>
      <div className="text-sm text-foreground font-medium">
        {snapshot?.display_label ?? "(no label)"}
      </div>
      {snapshot?.content && snapshot.content !== snapshot.display_label && (
        <div className="text-xs text-muted-foreground mt-1">
          {snapshot.content}
        </div>
      )}
      {id && (
        <div className="text-[10px] text-muted-foreground/60 font-mono mt-1.5 truncate">
          {id}
        </div>
      )}
    </div>
  );
}

function ResolutionButton({
  r,
  current,
  recommended,
  onClick,
  snapshot,
}: {
  r: Resolution;
  current: Resolution | "reject" | null;
  recommended: Resolution;
  onClick: () => void;
  snapshot?: { display_label?: string };
}) {
  const isRec = r === recommended;
  const isBusy = current === r;
  const label = (() => {
    if (r === "keep_both") return "Keep both";
    const which = r === "delete_a" ? "A" : "B";
    const hint = snapshot?.display_label
      ? ` (${truncate(snapshot.display_label, 24)})`
      : "";
    return `Delete ${which}${hint}`;
  })();

  return (
    <Button
      size="sm"
      variant={isRec ? "default" : "outline"}
      onClick={onClick}
      disabled={current !== null}
      className={cn(
        isRec && "bg-slate-900 text-white hover:bg-slate-800",
        !isRec && "bg-card"
      )}
    >
      {isBusy ? (
        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
      ) : (
        <Check className="h-3.5 w-3.5 mr-1.5" />
      )}
      {label}
      {isRec && (
        <span className="ml-1.5 text-[10px] opacity-80">· recommended</span>
      )}
    </Button>
  );
}

function ResolvedBadge({
  kind,
  resolution,
}: {
  kind: "applied" | "rejected";
  resolution?: Resolution;
}) {
  if (kind === "applied") {
    return (
      <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        {resolution ? resolutionLabel(resolution) : "Applied"}
      </div>
    );
  }
  return (
    <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
      Dismissed
    </div>
  );
}

function resolutionLabel(r: Resolution): string {
  switch (r) {
    case "keep_both":
      return "Kept both";
    case "delete_a":
      return "Deleted A";
    case "delete_b":
      return "Deleted B";
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
